import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import React from "react";

import {
  dualDelivery,
  enableHaystackOnly,
  isHaystackRequestBreadcrumb,
  shouldDualReport,
  shouldReportToHaystackOnly,
} from "../../helpers/haystackDelivery";

const API_KEY = "50f1b71460580af8963f749ae93d2c1d";

// Runtime config from /env-config.js. Absent: flags off, no token.
const runtimeConfig = window.__HAYSTACK_CONFIG__;
const HAYSTACK_AUTH_TOKEN = runtimeConfig?.authToken || "";
const HAYSTACK_PRODUCT = runtimeConfig?.product || "firehydrant";
// Dual reporting: Bugsnag and Haystack.
const HAYSTACK_DUAL_REPORT_ENABLED = runtimeConfig?.dualReportEnabled === true;
// Haystack only: nothing is sent to Bugsnag. Overrides dual reporting; needs a token.
const HAYSTACK_ONLY_ENABLED = runtimeConfig?.onlyEnabled === true;

// Build version: the full commit SHA.
const APP_VERSION = process.env.REACT_APP_VERSION;

let ErrorBoundary;
let env = null;

// TODO:
// This is a hack and we should implement it with env variables on deployments
if (
  window?.location?.hostname?.indexOf("staging.firehydrant.io") > -1 ||
  window?.location?.hostname?.indexOf("boblad.com") > -1
) {
  env = "staging";
} else if (
  window?.location?.hostname?.indexOf("status.local.firehydrant.io") === -1 &&
  window?.location?.hostname?.indexOf("localhost") === -1
) {
  env = "production";
}

// Every production cluster is "production" here, so which ones report is decided by the flags
// served at /env-config.js (dual reporting is on for all production clusters: US, AU, EU and IN).
const isProduction = env === "production";
const haystackEndpoint = `https://rum${isProduction ? "" : ".stage"}.haystack.es/${HAYSTACK_PRODUCT}/_logs`;

if (env) {
  const haystackOptions = {
    endpoint: haystackEndpoint,
    authToken: HAYSTACK_AUTH_TOKEN,
    appMeta: {
      appType: "nunc-status-pages",
      appVersion: APP_VERSION,
    },
  };

  // No session tracking in Haystack-only mode: start() would ping Bugsnag first.
  const shouldUseHaystackOnly = shouldReportToHaystackOnly({
    isProduction,
    flagEnabled: HAYSTACK_ONLY_ENABLED,
    authToken: HAYSTACK_AUTH_TOKEN,
  });

  const bugsnagClient = Bugsnag.start({
    releaseStage: env,
    apiKey: API_KEY,
    plugins: [new BugsnagPluginReact()],
    ...(shouldUseHaystackOnly && { autoTrackSessions: false }),
  });

  // True once Haystack delivery is installed.
  let haystackActive = shouldUseHaystackOnly && enableHaystackOnly(bugsnagClient, { haystackOptions });

  // Dual reporting (skipped when Haystack-only is active).
  if (
    !haystackActive &&
    HAYSTACK_AUTH_TOKEN &&
    shouldDualReport({ isProduction, flagEnabled: HAYSTACK_DUAL_REPORT_ENABLED })
  ) {
    try {
      const realBugsnagDelivery = bugsnagClient._delivery;

      bugsnagClient._setDelivery(
        dualDelivery({
          bugsnagDelivery: realBugsnagDelivery,
          haystackOptions,
        }),
      );
      haystackActive = true;
    } catch (err) {
      console.warn("[haystack] dual delivery setup failed", err);
    }
  }

  const BugsnagErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);

  // Our own Haystack requests are not breadcrumbs, in Haystack or Bugsnag.
  if (haystackActive) {
    Bugsnag.addOnBreadcrumb(
      (breadcrumb) => !isHaystackRequestBreadcrumb(breadcrumb, haystackEndpoint),
    );
  }

  ErrorBoundary = BugsnagErrorBoundary;
} else {
  ErrorBoundary = ({ children }) => <>{children}</>;
}

export default ErrorBoundary;
